import { SheetClose } from "@/shared/components/ui/sheet"
import { cn } from "@/utils/helpers"
import { faSpinnerThird } from "@awesome.me/kit-44b29310a6/icons/classic/regular"
import { faStar } from "@awesome.me/kit-44b29310a6/icons/classic/solid"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button } from "core/components/Button"
import { Tag } from "core/components/Tag"
import { useTimeFromNowShort } from "core/hooks/useTimeFromNowShort"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import { useTranslation } from "next-i18next"
import Link from "next/link"
import { useDeleteNotification } from "../hooks/useDeleteNotification"
import { useMarkNotificationAsRead } from "../hooks/useMarkNotificationAsRead"
import { useNotificationMetadata } from "../hooks/useNotificationMetadata"
import { TNotification } from "../types/notification"
import { NotificationActionButton } from "./NotificationActionButton"
import { CompanyAvatar } from "@/core/components/table/CompanyAvatar"
import { sanitizeUntrustedHtml } from "@/utils/sanitizeUntrustedHtml"
import { Icon } from "@/core/components/icon/Icon"
import { Avatar } from "@/core/components/Avatars"

import { useRecoilValue } from "recoil"
import { selectedSpaceAtom } from "@/atoms/spaces"
import { RFSCustomType } from "@/modules/tasks/active/constants/rfsCustomType"
dayjs.extend(relativeTime)

type TNotificationCardProps = {
  notification: TNotification
}

export const NotificationCard = ({ notification }: TNotificationCardProps) => {
  const { t } = useTranslation("notifications")
  const selectedSpace = useRecoilValue(selectedSpaceAtom)
  const { title, tagLabel, tagVariant } = useNotificationMetadata(notification)
  const markNotificationAsRead = useMarkNotificationAsRead()
  const {
    isLoading,
    isDeleted,
    canUndo,
    deleteNotification,
    undoDeleteNotification,
  } = useDeleteNotification()
  const timeFromNowShort = useTimeFromNowShort(notification.created)

  const dateStamp =
    selectedSpace.customType === RFSCustomType.DSV
      ? dayjs(notification.created).format("DD-MM-YYYY HH:mm")
      : timeFromNowShort
  const userAvatar = notification?.avatar_filename
    ? notification.avatar_filename
    : notification?.candidate?.email
      ? notification.candidate.email
      : notification._user?.email

  return (
    <div
      className={cn(
        "relative overflow-hidden transition-all duration-700",
        isDeleted ? (canUndo ? "max-h-16" : "max-h-0") : "max-h-96",
      )}
    >
      <div
        className={cn(
          "absolute bottom-0 left-0 right-0 top-0 flex items-center justify-between border-b border-solid border-gray-200 pl-4 pr-1",
          "transition-opacity duration-700 ease-in-out",
          isDeleted
            ? canUndo
              ? "opacity-100"
              : "opacity-0"
            : "pointer-events-none opacity-0",
        )}
      >
        <div className="text-md">{t("notification_deleted")}</div>
        <Button
          variant="text"
          onClick={() => undoDeleteNotification(notification.uid)}
        >
          {t("actions.undo_delete_notification")}
        </Button>
      </div>
      <SheetClose
        asChild
        className={cn(
          "transition-opacity duration-500",
          isDeleted ? "pointer-events-none opacity-0" : "opacity-100",
        )}
      >
        <Link
          href={`/r/${notification.link}`}
          onClick={() => markNotificationAsRead(notification.uid)}
        >
          <div className="hover:bg-primary/5 flex cursor-pointer space-x-2 border-b border-solid border-gray-200 bg-white py-4 pl-2 pr-1">
            <div>
              <div className="flex items-center">
                <div
                  className={`mr-2 h-2 w-2 rounded-full ${!notification.is_read && "bg-red-400"}`}
                />
                <div className="relative">
                  {notification.sender_name === "SYSTEM" && (
                    <CompanyAvatar
                      className="h-6 w-6 rounded-full bg-transparent object-contain"
                      spaceId={notification.task?.customer?.uid}
                      alt={notification.task?.customer?.name}
                      width={40}
                      height={40}
                      fallbackElement={
                        <div className="bg-primary-container text-on-primary-container flex h-6 max-h-6 w-6 max-w-6 items-center justify-center rounded-full p-2">
                          <Icon icon="corporate_fare" size="1rem" />
                        </div>
                      }
                    />
                  )}
                  {notification.sender_name !== "SYSTEM" && (
                    <Avatar email={userAvatar} size={"md"} />
                  )}
                  {notification.is_owner && (
                    <div className="absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-white">
                      <FontAwesomeIcon
                        className="text-orange-400"
                        icon={faStar}
                        size="xs"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-1 flex-col">
              {title !== undefined && (
                <div
                  className="text-md mb-2 text-left"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeUntrustedHtml(title),
                  }}
                />
              )}
              {tagLabel !== undefined && tagVariant !== undefined && (
                <div className="flex items-start">
                  <Tag label={tagLabel} variant={tagVariant} hasBadge />
                </div>
              )}
              {notification.task && (
                <div className="mt-2 flex rounded-md border border-solid border-gray-300 p-3">
                  {notification.task?.customer?.uid !== undefined && (
                    <div className="mr-1.5 h-9 w-9 items-center justify-items-center overflow-hidden rounded-full pt-1">
                      <CompanyAvatar
                        className="h-8 w-8 rounded-full object-contain"
                        spaceId={notification.task?.customer?.uid}
                        alt={notification.task?.customer?.name}
                        width={64}
                        height={64}
                        fallbackElement={
                          <div className="bg-primary-container text-on-primary-container flex h-8 max-h-8 w-8 max-w-8 items-center justify-center rounded-full p-2">
                            <Icon icon="corporate_fare" size="1rem" />
                          </div>
                        }
                      />
                    </div>
                  )}
                  <div>
                    <div className="text-md line-clamp-1 text-gray-400">
                      {/* TODO Re-implement case number when we have it standardized */}
                      {/* {notification.task?.case_number} &middot;{" "} */}
                      {notification.task.customer?.name}
                    </div>
                    <div className="text-md line-clamp-1 font-medium text-gray-600">
                      {notification.task.title}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div
              className={cn(
                "flex flex-col items-center",
                selectedSpace.customType === RFSCustomType.DSV &&
                  "items-end pr-2",
              )}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-sm text-gray-400">{dateStamp}</div>
              <NotificationActionButton
                notification={notification}
                deleteNotification={deleteNotification}
              />
            </div>
          </div>
        </Link>
      </SheetClose>
      <div
        className={cn(
          "absolute bottom-0 left-0 right-0 top-0 flex items-center justify-center bg-white transition-all duration-700",
          isLoading ? "opacity-80" : "pointer-events-none opacity-0",
        )}
      >
        <FontAwesomeIcon size="lg" icon={faSpinnerThird} spin />
      </div>
    </div>
  )
}
